/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2014, Bulb Inc.  For more information, please email contact@hellobulb.com.
*/

/**
* A directive that prevents the user from entering (e.g. typing, pasting, dropping) newline characters into a textarea
*/
angular.module('bulbApp.utilsModule').directive('bulbDisallowNewline', ['$timeout', bulbDisallowNewline]);

function bulbDisallowNewline($timeout) {

  /**
   * Replaces each newline character sequence in the specified text with a replacement string.
   * @param text The text string in which to search/replace newlines
   * @param replacementString  The replacement string - defaults to a single space
   * @returns The filtered string if newlines were present, or false otherwise
   */
  function replaceNewlines(text, replacementString) {
    replacementString = replacementString || ' ';
    return (text.match(/[\n\r]/)) ? text.replace(/[\n\r]+/g, replacementString) : false;
  }

  return {
    restrict: 'A',
    require: '?ngModel',
    link: function link(scope, elem, attrs, ngModelController) {
      elem.on('keydown keyup', (event) => {
        if (event.which === 13) {
          event.preventDefault();
        }
      });
      elem.on('paste', (jqEvent) => {
        const { clipboardData } = jqEvent.originalEvent;
        const pasteText = clipboardData
          ? clipboardData.getData('text/plain') : window.clipboardData.getData('text');
        let filteredPasteText = replaceNewlines(pasteText);
        if (filteredPasteText) {
          // We can't reliably change the data to be pasted, so instead we swallow the paste event
          // and do our own "insertion" at the current selection
          jqEvent.preventDefault();
          $timeout(() => {
            const htmlElement = elem.get(0);
            const { selectionStart } = htmlElement;
            const { selectionEnd } = htmlElement;
            const value = elem.val();

            const maxLength = parseInt(elem.attr('maxlength'), 10) || Infinity;
            const currentLength = value.length - (selectionEnd - selectionStart);
            const allowedLength = maxLength - currentLength;

            if (allowedLength <= 0) {
              filteredPasteText = '';
            } else if (filteredPasteText.length > allowedLength) {
              filteredPasteText = filteredPasteText.substring(0, allowedLength);
            }

            const filteredValue = value.substring(0, selectionStart) + filteredPasteText
              + value.substring(htmlElement.selectionEnd);
            elem.val(filteredValue);
            if (ngModelController) {
              ngModelController.$setViewValue(filteredValue);
            }
            // Set the cursor to the end of the 'pasted' filtered text
            htmlElement.selectionEnd = selectionStart + filteredPasteText.length;
          });
        }
      });
      elem.on('drop', (jqEvent) => {
        const { dataTransfer } = jqEvent.originalEvent;
        const dropText = dataTransfer.getData('text');
        const filteredDropText = replaceNewlines(dropText);
        if (filteredDropText) {
          /*
           FUTURE: Ideally, we'd like to:

           1. Intercept and filter the dataTransfer text within this drop event handler. But browsers
           won't let us call dataTransfer.setData('text', filteredDropText); for security reasons.

           2. If we could browser-compatibly get the drop cursor position (elem.get(0).selectionStart),
           we could abort the drop and insert the filtered text ourselves.  No dice yet (except for IE!)

           Fallback for now is to catch the 'input' event that follows the drop, and filter the entire
           elem.val() at that point. We then have to restore the selection, and since Ctrl+Z (Undo)
           re-introduces the newlines on some browsers (looking at you Firefox), we'll also need a
           safety net 'blur' handler that strips any missed or re-introduced newlines.
           */

          // Intercept the next 'input' event (which always follows a drop)
          elem.one('input', () => {
            // Re-filter the *entire* value at this point (pre-drop content with inserted dropped text)
            const value = elem.val();
            const filteredValue = replaceNewlines(value);
            $timeout(() => {
              elem.val(filteredValue);
              if (ngModelController) {
                ngModelController.$setViewValue(filteredValue);
              }
              elem.trigger('autosize.resize');

              // Grab the index of the dropText that includes newlines, to handle
              // the case where filteredDropText is already a substring of value.
              const dropIndex = value.search(replaceNewlines(dropText, '\\n'));
              // Re-select the filtered version of the dropped text
              const htmlElement = elem.get(0);
              htmlElement.focus(); // for Firefox's sake
              htmlElement.setSelectionRange(dropIndex, dropIndex + filteredDropText.length);
            });
          });
        }
      });

      // This safety net strips any newlines that might have snuck in despite our best efforts
      elem.on('blur', () => {
        const filteredValue = replaceNewlines(elem.val());
        if (filteredValue) {
          $timeout(() => {
            elem.val(filteredValue);
            if (ngModelController) {
              ngModelController.$setViewValue(filteredValue);
            }
            elem.trigger('autosize.resize');
          });
        }
      });
    },
  };
}
